import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Group,
  Text,
  Badge,
  ActionIcon,
  Collapse,
  Skeleton,
  Paper,
  Divider,
  Tooltip,
  Box,
  Flex,
  Progress,
} from "@mantine/core";
import {
  IconFileInvoice,
  IconClock,
  IconAlertTriangle,
  IconChevronDown,
  IconChevronUp,
  IconRefresh,
  IconShieldCheck,
  IconCoins,
  IconScale,
} from "@tabler/icons-react";
import { repositorySign } from "../_base/_const/_constVar";
import { NotificationExtension } from "../_base/extension/NotificationExtension";
import { ContractSummaryModel, InstallmentPhaseModel } from "../model/ContractSummaryModel";
import { MessageResponse } from "../model/MessageResponse";

interface ContractSummaryPanelProps {
  documentId: number;
}

// Text PDF được ghép mỗi trang thành 1 dòng -> xuống dòng trước gạch đầu dòng "- <Chữ hoa>",
// mốc "Lần/Đợt N:" và câu phạt chậm cho dễ đọc.
const formatRawPaymentText = (text?: string | null): string =>
  (text || "")
    .replace(/\s+-\s+(?=[A-ZĐÀ-ÝĂƠƯẠ-Ỹ])/g, "\n- ")
    .replace(/\s*((?:-\s*)?(?:Lần|Đợt)\s+\d+\s*[:\-])/g, "\n$1")
    .replace(/\s*((?:-\s*)?(?:Trong\s+)?trường\s+hợp\s+chậm\s+thanh\s+toán)/gi, "\n$1")
    .trim();

export const ContractSummaryPanel: React.FC<ContractSummaryPanelProps> = ({ documentId }) => {
  const [data, setData] = useState<ContractSummaryModel | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [opened, setOpened] = useState<boolean>(true);

  const fetchSummary = useCallback(
    async (forceReload: boolean = false) => {
      if (!documentId || documentId <= 0) return;
      try {
        setLoading(true);
        const url = `/api/v1/ContractSummary/get-or-extract?documentId=${documentId}${
          forceReload ? "&forceReload=true" : ""
        }`;
        const response = await repositorySign.get<MessageResponse<ContractSummaryModel>>(url);

        if (response?.success && response.data) {
          setData(response.data);
          if (forceReload) {
            NotificationExtension.Success("Đã làm mới dữ liệu tóm tắt hợp đồng từ file PDF!");
          }
        } else {
          setData(null);
        }
      } catch (err: any) {
        console.error("Lỗi khi tải tóm tắt hợp đồng:", err);
      } finally {
        setLoading(false);
      }
    },
    [documentId]
  );

  useEffect(() => {
    fetchSummary(false);
  }, [fetchSummary]);

  if (!documentId || documentId <= 0) return null;

  if (loading && !data) {
    return (
      <Card withBorder radius="md" p="md" mb="md" shadow="xs" style={{ borderColor: "#cbd5e1" }}>
        <Group justify="space-between" mb="xs">
          <Skeleton height={20} width={200} radius="sm" />
          <Skeleton height={20} width={80} radius="sm" />
        </Group>
        <Skeleton height={50} radius="sm" mb="xs" />
        <Skeleton height={60} radius="sm" />
      </Card>
    );
  }

  if (!data) return null;

  // Nếu tài liệu không phải là hợp đồng (không có tổng tiền và không có bất kỳ đợt thanh toán nào) -> Không hiển thị thẻ
  if (data.status === "NOT_A_CONTRACT" || (!data.totalAmount && (!data.paymentTerms || data.paymentTerms.length === 0))) {
    return null;
  }

  const hasPaymentTerms = !!data.paymentTerms && data.paymentTerms.length > 0;
  const isInstallment = hasPaymentTerms && data.paymentTerms.length >= 2;
  const rawPaymentText = formatRawPaymentText(data.rawPaymentText);
  const fullAmount = hasPaymentTerms
    ? data.paymentTerms[0].amountFormatted ||
      data.totalAmountFormatted ||
      (data.totalAmount != null ? data.totalAmount.toLocaleString("vi-VN") + " đồng" : "")
    : "";
  const fullAmountWords = hasPaymentTerms ? data.paymentTerms[0].amountWords || data.totalAmountWords : "";

  return (
    <Card
      withBorder
      radius="md"
      p={0}
      mb="md"
      shadow="xs"
      style={{
        borderColor: "#cbd5e1",
        backgroundColor: "#ffffff",
        overflow: "hidden",
      }}
    >
      {/* Header Bar */}
      <Box
        px="sm"
        py={8}
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          color: "#ffffff",
        }}
      >
        <Flex justify="space-between" align="center" wrap="wrap" gap="xs">
          <Group gap="xs">
            <IconFileInvoice size={18} color="#60a5fa" />
            <Text fw={700} size="xs" c="#ffffff">
              Tóm tắt thông tin hợp đồng
            </Text>

            <Badge
              variant="filled"
              color={isInstallment ? "teal" : "blue"}
              size="xs"
              leftSection={<IconClock size={10} />}
            >
              {isInstallment ? `Trả góp (${data.paymentTerms?.length || 0} đợt)` : "Thanh toán 100%"}
            </Badge>

            {data.isMatchedWordsAndDigits ? (
              <Badge variant="light" color="green" size="xs" leftSection={<IconShieldCheck size={10} />}>
                Khớp số & chữ
              </Badge>
            ) : data.totalAmount != null && data.totalAmountWords ? (
              <Badge variant="filled" color="orange" size="xs" leftSection={<IconAlertTriangle size={10} />}>
                Cần đối soát
              </Badge>
            ) : null}
          </Group>

          <Group gap={6}>
            <Tooltip label="Trích xuất lại từ các file PDF" position="bottom" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                loading={loading}
                onClick={() => fetchSummary(true)}
                aria-label="Làm mới"
                style={{ color: "#e2e8f0" }}
              >
                <IconRefresh size={14} />
              </ActionIcon>
            </Tooltip>

            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => setOpened((prev) => !prev)}
              aria-label="Thu gọn / Mở rộng"
              style={{ color: "#e2e8f0" }}
            >
              {opened ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            </ActionIcon>
          </Group>
        </Flex>
      </Box>

      {/* Content Area */}
      <Collapse in={opened}>
        <Box p="sm" style={{ fontSize: "12.5px", lineHeight: "1.6", color: "#1e293b" }}>
          
          {/* Section 1: Tổng giá trị hợp đồng */}
          {data.totalAmount != null && (
            <Paper
              withBorder
              p="xs"
              radius="sm"
              mb="xs"
              style={{
                backgroundColor: "#f8fafc",
                borderColor: "#e2e8f0",
              }}
            >
              <Flex align="baseline" wrap="wrap" gap="xs" mb={2}>
                <Text fw={700} size="xs" c="#1e293b">
                  1. Tổng giá trị hợp đồng:
                </Text>
                <Text fw={800} size="sm" c="#1d4ed8">
                  {data.totalAmountFormatted || (data.totalAmount.toLocaleString("vi-VN") + " đồng")}
                </Text>
              </Flex>

              {data.totalAmountWords && (
                <Text size="xs" c="#475569" style={{ fontStyle: "italic", paddingLeft: "12px" }}>
                  (Bằng chữ: <strong style={{ fontStyle: "normal", color: "#0f172a" }}>{data.totalAmountWords}</strong>)
                </Text>
              )}
            </Paper>
          )}

          {/* Section 2: Thời hạn thanh toán */}
          {(hasPaymentTerms || rawPaymentText) && (
            <Paper
              withBorder
              p="xs"
              radius="sm"
              mb="xs"
              style={{
                backgroundColor: "#ffffff",
                borderColor: "#e2e8f0",
              }}
            >
              <Text fw={700} size="xs" c="#1e293b" mb={6}>
                2. Thời hạn thanh toán:
              </Text>

              {/* Phân nhánh: Nhiều đợt (Installment) vs 1 đợt (100%) */}
              {!hasPaymentTerms ? null : isInstallment ? (
                <Box style={{ paddingLeft: "12px" }}>
                  {data.paymentTerms.map((phase: InstallmentPhaseModel, idx: number) => (
                    <Box key={idx} mb={6} style={{ borderLeft: "2px solid #3b82f6", paddingLeft: "8px" }}>
                      <Text size="xs" fw={700} c="#1e293b">
                        - {phase.phaseName || `Lần ${phase.phaseIndex || idx + 1}`}:{" "}
                        <span style={{ fontWeight: 600, color: "#2563eb" }}>
                          thanh toán {phase.percentage != null ? `${phase.percentage}%` : ""}{" "}
                          giá trị hợp đồng, tương ứng {phase.amountFormatted || (phase.amount ? phase.amount.toLocaleString("vi-VN") + " đồng" : "")}
                        </span>
                        {phase.amountWords && (
                          <span style={{ color: "#64748b", fontWeight: "normal", fontStyle: "italic" }}>
                            {" "}(Bằng chữ: {phase.amountWords})
                          </span>
                        )}
                      </Text>
                      {phase.condition && (
                        <Text size="xs" c="#475569" mt={2} style={{ lineHeight: 1.4 }}>
                          {phase.condition}
                        </Text>
                      )}
                    </Box>
                  ))}

                  {/* Progress bar visual for cashflow */}
                  <Box mt="xs" mb={4}>
                    <Progress.Root size="sm" radius="xl" style={{ backgroundColor: "#e2e8f0" }}>
                      {data.paymentTerms.map((phase: InstallmentPhaseModel, idx: number) => {
                        const colors = ["blue", "teal", "indigo", "cyan", "violet"];
                        return (
                          <Progress.Section
                            key={idx}
                            value={phase.percentage || 0}
                            color={colors[idx % colors.length]}
                          >
                            <Progress.Label>{phase.percentage ? `${phase.percentage}%` : ""}</Progress.Label>
                          </Progress.Section>
                        );
                      })}
                    </Progress.Root>
                  </Box>
                </Box>
              ) : (
                <Box style={{ borderLeft: "2px solid #2563eb", paddingLeft: "10px" }}>
                  <Text size="xs" fw={700} c="#1e293b">
                    Thanh toán 100% giá trị hợp đồng
                    {fullAmount && (
                      <>
                        , tương ứng <span style={{ fontWeight: 600, color: "#2563eb" }}>{fullAmount}</span>
                      </>
                    )}
                    {fullAmountWords && (
                      <span style={{ color: "#64748b", fontWeight: "normal", fontStyle: "italic" }}>
                        {" "}(Bằng chữ: {fullAmountWords})
                      </span>
                    )}
                  </Text>
                  {data.paymentTerms[0].condition && (
                    <Text size="xs" c="#475569" mt={2} style={{ lineHeight: 1.4 }}>
                      {data.paymentTerms[0].condition}
                    </Text>
                  )}
                </Box>
              )}

              {/* Nguyên văn khoản "Thời hạn thanh toán" trong hợp đồng: nguồn đối chiếu khi phần tách tự động thiếu/sai */}
              {rawPaymentText && (
                <Box
                  mt={hasPaymentTerms ? 8 : 0}
                  px={8}
                  py={6}
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderLeft: "3px solid #94a3b8",
                    borderRadius: 4,
                  }}
                >
                  <Text size="xs" fw={600} c="#64748b" mb={2}>
                    Nguyên văn trong hợp đồng:
                  </Text>
                  <Text size="xs" c="#334155" style={{ whiteSpace: "pre-line", lineHeight: 1.55 }}>
                    {rawPaymentText}
                  </Text>
                </Box>
              )}
            </Paper>
          )}

          {/* Section 3: Phạt chậm thanh toán (Nếu có) */}
          {data.latePaymentPenalty && (
            <Paper
              withBorder
              p="xs"
              radius="sm"
              style={{
                backgroundColor: "#fffbeb",
                borderColor: "#fde68a",
              }}
            >
              <Group gap={6} align="center">
                <IconScale size={14} color="#b45309" />
                <Text size="xs" fw={700} c="#92400e">
                  3. Phạt chậm thanh toán:
                </Text>
                <Text size="xs" fw={700} c="#b45309">
                  {data.latePaymentPenalty}
                </Text>
              </Group>
            </Paper>
          )}

        </Box>
      </Collapse>
    </Card>
  );
};
export default ContractSummaryPanel;
